import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ApiKeys from './pages/ApiKeys';
import Pricing from './pages/Pricing';
import Settings from './pages/Settings';
import Success from './pages/Success';
import Cancel from './pages/Cancel';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import ConsoleProtectedRoute from './components/ConsoleProtectedRoute';
import ToastWrapper from './components/ToastWrapper';
import Documentation from './components/Documentation';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Billing from './pages/Billing';
import Support from './pages/Support';
import ConsoleLogin from './pages/ConsoleLogin';
import ConsoleDashboard from './pages/ConsoleDashboard';
import InactivityWarningModal from './components/InactivityWarningModal';
import useInactivityTimer from './hooks/useInactivityTimer';
import { isAuthenticated, removeToken } from './utils/auth';
import toast from 'react-hot-toast';

function AppContent() {
  const navigate = useNavigate();

  // Only enable auto-logout for authenticated users
  const isUserAuthenticated = isAuthenticated();

  const handleLogout = () => {
    removeToken();
    toast.error('You were logged out due to inactivity');
    navigate('/login');
  };

  const handleWarning = () => {
    toast('Session timeout warning', {
      icon: '⚠️',
      duration: 2000,
    });
  };

  const { showWarning, timeRemaining, stayLoggedIn } = useInactivityTimer({
    timeout: 8 * 60 * 60 * 1000, // 8 hours
    warningTime: 5 * 60 * 1000, // 5 minutes warning
    onWarning: handleWarning,
    onLogout: handleLogout,
    enabled: isUserAuthenticated // Only enable for authenticated users
  });

  return (
    <>
      <ToastWrapper />
      <InactivityWarningModal
        isOpen={showWarning}
        timeRemaining={timeRemaining}
        onStayLoggedIn={stayLoggedIn}
        onLogout={handleLogout}
      />
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />

        {/* Email & Password Management */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legal Pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/support" element={<Support />} />

        {/* CONSOLE ROUTES */}
        <Route path="/console/login" element={<ConsoleLogin />} />
        <Route
          path="/console"
          element={
            <ConsoleProtectedRoute>
              <ConsoleDashboard />
            </ConsoleProtectedRoute>
          }
        />

        {/* PROTECTED ROUTES */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-keys"
          element={
            <ProtectedRoute>
              <ApiKeys />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;